import type { Trip } from '@my-fullstack-app/itinerary-types';

// Great-circle distance lives in the shared lib so the API and mobile
// share one implementation (ADR-0090). Re-exported here for existing
// call sites that import it from `../lib/distance`.
export { haversineMeters } from '@my-fullstack-app/itinerary-types';

export type DistanceUnit = 'km' | 'mi';

const METERS_PER_MILE = 1609.344;

/**
 * Render a distance in meters as a short user-facing string in the
 * user's preferred unit. Picks a sensible precision per magnitude:
 *
 *   km: < 1000 m → "350 m"; ≥ 1 km → "1.2 km" (one decimal), ≥ 10 km → "12 km"
 *   mi: < 0.1 mi (~160 m) → "100 m"; otherwise → "0.3 mi" / "12 mi"
 *
 * Returns '' for non-finite / negative input so callers can render
 * nothing without special-casing.
 */
export function formatDistance(meters: number, unit: DistanceUnit): string {
  if (!Number.isFinite(meters) || meters < 0) return '';
  if (unit === 'mi') {
    const miles = meters / METERS_PER_MILE;
    if (miles < 0.1) return `${Math.round(meters)} m`;
    if (miles < 10) return `${miles.toFixed(1)} mi`;
    return `${Math.round(miles)} mi`;
  }
  if (meters < 1000) return `${Math.round(meters)} m`;
  const km = meters / 1000;
  if (km < 10) return `${km.toFixed(1)} km`;
  return `${Math.round(km)} km`;
}

/**
 * Centroid of a trip's geocoded places — used as the trip anchor for
 * "X km from home" rollups. Averages lat/lng across every Place that
 * has coords, across every day. Returns `null` when no place in the
 * trip is geocoded (typical for brand-new trips before the user adds
 * a Place from Google).
 */
export function tripCentroid(trip: Trip): { lat: number; lng: number } | null {
  let sumLat = 0;
  let sumLng = 0;
  let count = 0;
  for (const day of trip.days) {
    for (const place of day.places) {
      if (typeof place.lat === 'number' && typeof place.lng === 'number'
          && Number.isFinite(place.lat) && Number.isFinite(place.lng)) {
        sumLat += place.lat;
        sumLng += place.lng;
        count++;
      }
    }
  }
  if (count === 0) return null;
  return { lat: sumLat / count, lng: sumLng / count };
}

/**
 * Best anchor point for "X km from home" rollups. Prefers the picked
 * `destinationPlace` coords (set at trip creation via the new-trip
 * picker — ADR-0054) so the line renders before any Place is added;
 * falls back to `tripCentroid` once Places exist. Returns null when
 * neither is available.
 */
export function tripAnchor(trip: Trip): { lat: number; lng: number } | null {
  const dp = trip.destinationPlace;
  if (dp && typeof dp.lat === 'number' && typeof dp.lng === 'number'
      && Number.isFinite(dp.lat) && Number.isFinite(dp.lng)) {
    return { lat: dp.lat, lng: dp.lng };
  }
  return tripCentroid(trip);
}
